import { useRef, useState } from "react";
import type { Citation } from "@/apis/cs-rag/types";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { popModal } from "@/stores/modal";
import { RotateCcw, X, ZoomIn, ZoomOut } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Zoom } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/zoom";

const ZOOM_STEP = 1.4;
const MIN_SCALE = 0.3;
const MAX_SCALE = 5;

interface CitationViewerModalProps {
  citation: Citation;
}

export const CitationViewerModal: React.FC<CitationViewerModalProps> = ({
  citation,
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const scaleRef = useRef(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = citation.images ?? [];
  const hasImages = images.length > 0;
  const hasMultipleImages = images.length > 1;

  const handleZoomIn = () => {
    const swiper = swiperRef.current;
    if (!swiper?.zoom) return;
    const next = Math.min(scaleRef.current * ZOOM_STEP, MAX_SCALE);
    scaleRef.current = next;
    swiper.zoom.in(next);
  };

  const handleZoomOut = () => {
    const swiper = swiperRef.current;
    if (!swiper?.zoom) return;
    const next = Math.max(scaleRef.current / ZOOM_STEP, MIN_SCALE);
    scaleRef.current = next;
    if (next <= 1) {
      swiper.zoom.out();
      scaleRef.current = 1;
    } else {
      swiper.zoom.in(next);
    }
  };

  const handleReset = () => {
    const swiper = swiperRef.current;
    if (!swiper?.zoom) return;
    scaleRef.current = 1;
    swiper.zoom.out();
  };

  return (
    <ModalWrapper className="flex flex-col w-[90vw] h-[85vh] max-w-[1200px] rounded-[10px] border border-line-normal bg-background-base shadow-lg overflow-hidden">
      {/* 헤더 */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-line-alternative">
        <span className="shrink-0 min-w-[24px] px-2 py-0.5 rounded-full bg-blue-600 text-white caption-1-semibold text-center">
          {citation.n}
        </span>
        <div className="flex-1 min-w-0">
          <p className="label-1-normal-semibold text-label-normal truncate">
            출처 {citation.n}
          </p>
          <p className="caption-2-regular text-label-alternative">
            p.{citation.page}
          </p>
        </div>

        {hasImages && (
          <div className="shrink-0 flex items-center gap-1">
            <ModalIconButton
              icon={<ZoomIn className="size-4" />}
              label="확대"
              onClick={handleZoomIn}
            />
            <ModalIconButton
              icon={<ZoomOut className="size-4" />}
              label="축소"
              onClick={handleZoomOut}
            />
            <ModalIconButton
              icon={<RotateCcw className="size-4" />}
              label="맞춤"
              onClick={handleReset}
            />
          </div>
        )}

        <button
          type="button"
          onClick={() => popModal()}
          aria-label="닫기"
          className="shrink-0 size-8 flex items-center justify-center rounded-md text-label-alternative hover:text-label-normal hover:bg-background-neutral-neutral transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {hasImages ? (
          <div className="h-full px-6 py-4">
            <Swiper
              key={citation.n}
              modules={[Navigation, Zoom]}
              navigation={hasMultipleImages}
              zoom={{ maxRatio: MAX_SCALE, minRatio: MIN_SCALE }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                scaleRef.current = 1;
              }}
              onSlideChange={(swiper) => {
                setCurrentIndex(swiper.activeIndex);
                scaleRef.current = 1;
              }}
              allowTouchMove={hasMultipleImages}
              className="h-full rounded-lg border border-line-alternative bg-white"
            >
              {images.map((image, i) => (
                <SwiperSlide key={i}>
                  <div className="swiper-zoom-container">
                    <img
                      src={image.data}
                      alt={`${citation.title} - p.${citation.page}${images.length > 1 ? ` (${i + 1}/${images.length})` : ""}`}
                      className="block w-full"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {hasMultipleImages && (
              <div className="mt-2 text-center">
                <span className="caption-1-semibold text-label-alternative">
                  {currentIndex + 1} / {images.length}
                </span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center body-2-normal-regular text-label-assistive py-12">
            이 출처에는 페이지 이미지가 없습니다
          </p>
        )}
      </div>
    </ModalWrapper>
  );
};

const ModalIconButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="size-8 flex items-center justify-center rounded-md text-label-alternative hover:text-label-normal hover:bg-background-neutral-neutral transition-colors"
  >
    {icon}
  </button>
);
